import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { IconFolder, IconSearch, IconVideo } from "@tabler/icons-react";
import { VideoDto } from "@repo/api/video";
import { FolderDto } from "@repo/api/folder";
import { Input } from "@/lib/components/Input";
import Image from "@/lib/components/Image";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import {
  useSearchVideosQuery,
  usePagedVideosQuery,
} from "@/lib/queries/video-queries";
import { useFolderContentsQuery } from "@/lib/queries/folder-queries";
import { cn, formatCreatedAt } from "@/lib/utils";
import { paths } from "@/App";

const SEARCH_DEBOUNCE_MS = 300;
const RECENT_VIDEOS_LIMIT = 8;
const RECENT_FOLDERS_LIMIT = 4;

export default function VideoSearchBar() {
  const { teamExternalId } = useParams();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const { data, isFetching, isError } = useSearchVideosQuery(
    teamExternalId!,
    debouncedSearch,
  );

  const videos = data?.videos ?? [];
  const folders = data?.folders ?? [];

  const hasQuery = debouncedSearch.length > 0;

  const pagedVideosQuery = usePagedVideosQuery(teamExternalId!);
  const recentVideos =
    pagedVideosQuery.data?.pages
      .flatMap((page) => page.videos)
      .slice(0, RECENT_VIDEOS_LIMIT) ?? [];

  const rootFolderContentsQuery = useFolderContentsQuery(teamExternalId!);
  const recentFolders =
    rootFolderContentsQuery.data?.folders.slice(0, RECENT_FOLDERS_LIMIT) ?? [];

  return (
    <div className="relative h-9 w-full max-w-full md:w-92">
      <div
        ref={containerRef}
        className={cn(
          "absolute inset-x-0 top-0 rounded-md",
          open &&
            "-inset-x-2.5 -top-2.5 z-50 rounded-lg bg-background p-2.5 pb-0 shadow-lg ring-1 ring-border",
        )}
      >
        <Input
          containerSize="sm"
          placeholder="Search videos and folders"
          leadingComponent={<IconSearch size={18} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          className={cn(!open && "border-grey-150 bg-grey-100")}
        />
        {open && (
          <>
            <VideoSearchResults
              teamExternalId={teamExternalId!}
              videos={hasQuery ? videos : recentVideos}
              folders={hasQuery ? folders : recentFolders}
              hasQuery={hasQuery}
              isFetching={
                hasQuery
                  ? isFetching
                  : pagedVideosQuery.isFetching ||
                    rootFolderContentsQuery.isFetching
              }
              isError={
                hasQuery
                  ? isError
                  : pagedVideosQuery.isError || rootFolderContentsQuery.isError
              }
              onSelect={() => setOpen(false)}
            />
          </>
        )}
      </div>
    </div>
  );
}

type VideoSearchResultsProps = {
  teamExternalId: string;
  videos: VideoDto[];
  folders: FolderDto[];
  hasQuery: boolean;
  isFetching: boolean;
  isError: boolean;
  onSelect: () => void;
};

function VideoSearchResults({
  teamExternalId,
  videos,
  folders,
  hasQuery,
  isFetching,
  isError,
  onSelect,
}: VideoSearchResultsProps) {
  if (isError) {
    return (
      <div className="flex min-h-71 flex-col items-center justify-center gap-2 px-3 py-4 text-center text-sm text-text-secondary">
        <IconSearch size={24} strokeWidth={1.5} />
        <span>Failed to search videos and folders</span>
      </div>
    );
  }

  if (!hasQuery && videos.length === 0 && folders.length === 0) {
    return (
      <div className="flex min-h-71 flex-col items-center justify-center gap-2 px-3 py-4 text-sm text-text-secondary">
        {isFetching ? (
          <LoadingSpinner size={18} />
        ) : (
          <>
            <IconSearch size={24} strokeWidth={1.5} />
            <span>Type to search videos</span>
          </>
        )}
      </div>
    );
  }

  if (videos.length === 0 && folders.length === 0) {
    return (
      <div className="flex min-h-71 flex-col items-center justify-center gap-2 px-3 py-4 text-sm text-text-secondary">
        {isFetching ? (
          <LoadingSpinner size={18} />
        ) : (
          <>
            <IconSearch size={24} strokeWidth={1.5} />
            <span>No videos or folders found</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="-mx-2 flex max-h-71 flex-col overflow-y-auto p-0.5 pt-1">
      {videos.map((video) => (
        <Link
          key={video.externalId}
          to={paths.videoEdit(teamExternalId, video.externalId)}
          onClick={onSelect}
          className="relative flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-grey-100 focus-visible:z-10 focus-visible:bg-grey-100 focus-visible:ring-2 focus-visible:ring-tertiary-400 focus-visible:outline-none"
        >
          <div className="relative aspect-video h-10 shrink-0 overflow-hidden rounded bg-gray-100">
            {video.thumbnailUrl ? (
              <Image
                src={video.thumbnailUrl}
                alt="Video thumbnail"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <IconVideo size={18} className="text-text-secondary" />
              </div>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <p className="line-clamp-1 text-sm font-medium">
              {video.name || "Untitled video"}
            </p>
            <p className="line-clamp-1 text-xs text-text-secondary">
              {formatCreatedAt(video.updatedAt, "Edited")}
            </p>
          </div>
        </Link>
      ))}
      {folders.map((folder) => (
        <Link
          key={folder.externalId}
          to={paths.videosFolder(teamExternalId, folder.externalId)}
          onClick={onSelect}
          className="relative flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-grey-100 focus-visible:z-10 focus-visible:bg-grey-100 focus-visible:ring-2 focus-visible:ring-tertiary-400 focus-visible:outline-none"
        >
          <div className="flex aspect-video h-10 shrink-0 items-center justify-center rounded bg-gray-100">
            <IconFolder size={18} className="text-text-secondary" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <p className="line-clamp-1 text-sm font-medium">{folder.name}</p>
            <p className="line-clamp-1 text-xs text-text-secondary">
              {formatCreatedAt(folder.updatedAt, "Edited")}
            </p>
          </div>
        </Link>
      ))}
    </div>
  );
}
